import * as React from 'react';
import * as Device from 'react-device-detect';

const isDev = import.meta.env.DEV;

export function usePreventClickOnScroll<T extends HTMLElement>(
  scrollRef: React.RefObject<T | null>,
  delay = 400 // a bit higher for iOS inertia
) {
  const disableClickRef = React.useRef(false);
  const timeoutRef = React.useRef<number | null>(null);
  const lastScrollTimeRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (Device.isDesktop) {
      // No need when not native where scroll and click handling is proper
      return;
    }

    const scrollEl: T | Window = scrollRef.current ?? window;

    const handleScroll = () => {
      const now = Date.now();
      lastScrollTimeRef.current = now;
      disableClickRef.current = true;

      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        disableClickRef.current = false;
        timeoutRef.current = null;
        isDev && console.log('[usePreventClickOnScroll] re-enable clicks at', Date.now());
      }, delay);
    };

    const handleClickCapture = (event: MouseEvent) => {
      const now = Date.now();
      const lastScroll = lastScrollTimeRef.current;
      const delta = lastScroll != null ? now - lastScroll : null;

      const target = event.target as HTMLElement | null;
      const bottomNavEl = target?.closest<HTMLElement>('[id^="bottomnav-tab"]');
      const byPass = Boolean(bottomNavEl) || !disableClickRef.current;

      isDev &&
        console.log('[usePreventClickOnScroll] click captured', {
          disableClick: byPass ? 'bypassed' : disableClickRef.current,
          deltaSinceLastScroll: delta,
          targetTag: target?.tagName,
          targetId: target?.id ?? '',
          matchedBottomNavId: bottomNavEl?.id ?? null,
        });

      if (byPass) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      isDev && console.log('[usePreventClickOnScroll] click blocked due to recent scroll');
    };

    // Attach scroll listener on the right container
    if (scrollEl instanceof Window) {
      scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    } else {
      scrollEl.addEventListener('scroll', handleScroll as any, { passive: true });
    }

    // Global click capture (runs before React synthetic events)
    document.addEventListener('click', handleClickCapture, true);

    return () => {
      if (scrollEl instanceof Window) {
        scrollEl.removeEventListener('scroll', handleScroll);
      } else {
        scrollEl.removeEventListener('scroll', handleScroll as any);
      }

      document.removeEventListener('click', handleClickCapture, true);

      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [delay, scrollRef]);
}
